import { useIsPropertiesDrawerOpen } from "@hooks/useSurveySelectors";
import { useEffect, useRef } from "react";

/**
 * Hook to prevent auto-trigger in SharePDFUtils when:
 * 1. User scrolls or clicks on builder-main element
 * 2. Properties drawer is opened in survey
 *
 * When triggered, sets a ref flag that prevents the outdated PDF auto-trigger
 * from firing in the useSurveyPDF hook (the 3-second auto-regenerate on status="outdated")
 *
 * @param shouldPreventAutoTriggerRef - Ref that will be set to true to prevent auto-trigger
 */
export const useCloseShareModalOnMapAction = (shouldPreventAutoTriggerRef: {
  current: boolean;
}) => {
  const prevDrawerStateRef = useRef<boolean>(false);
  const isPropertiesDrawerOpen = useIsPropertiesDrawerOpen();

  useEffect(() => {
    const handleBuilderMainInteraction = () => {
      shouldPreventAutoTriggerRef.current = true;
    };

    // Get builder-main element
    const builderMainElement = document?.querySelector(".builder-main");

    // Listen for scroll events on builder-main
    if (builderMainElement) {
      builderMainElement.addEventListener(
        "scroll",
        handleBuilderMainInteraction,
      );
    }

    // Listen for click events on builder-main
    if (builderMainElement) {
      builderMainElement.addEventListener(
        "click",
        handleBuilderMainInteraction,
      );
    }

    return () => {
      if (builderMainElement) {
        builderMainElement.removeEventListener(
          "scroll",
          handleBuilderMainInteraction,
        );
        builderMainElement.removeEventListener(
          "click",
          handleBuilderMainInteraction,
        );
      }
    };
  }, [shouldPreventAutoTriggerRef]);

  // Check if properties drawer is open
  useEffect(() => {
    // Prevent auto-trigger only when drawer changes from closed to open
    if (isPropertiesDrawerOpen && !prevDrawerStateRef.current) {
      shouldPreventAutoTriggerRef.current = true;
    }

    // Update ref for next comparison
    prevDrawerStateRef.current = isPropertiesDrawerOpen;
  }, [isPropertiesDrawerOpen, shouldPreventAutoTriggerRef]);
};
